import { useState } from "react";
import { toast } from "sonner";
import { ArrowRight, Newspaper, Mic2, Handshake, MessageCircle, HeartPulse, ArrowUpRight } from "lucide-react";
import { MaskedLine, FadeUp, Page, Meta } from "../components/Reveal";
import { Field, SubmitButton, inputCls } from "../components/forms";
import { postContact } from "../lib/api";
import { useLinks } from "../lib/LinksContext";
import { useContent } from "../lib/ContentContext";
import { SOCIAL_LINKS } from "../lib/socials";

const PATH_ICONS = [HeartPulse, Newspaper, Mic2, Handshake];

const initialForm = { name: "", email: "", subject: "", message: "" };

export default function Contact() {
  const c = useContent();
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const links = useLinks();
  const socials = SOCIAL_LINKS.filter((s) => links[s.key]);

  const PATHS = PATH_ICONS.map((Icon, i) => ({
    Icon,
    subject: c(`contact.chemin.${i + 1}.titre`),
    text: c(`contact.chemin.${i + 1}.texte`),
  }));

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const choosePath = (subject) => {
    setForm((f) => ({ ...f, subject }));
    document.getElementById("formulaire")?.scrollIntoView({ behavior: "smooth" });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await postContact(form);
      toast.success("Votre message a bien été envoyé. Merci !");
      setForm(initialForm);
    } catch {
      toast.error("Une erreur est survenue. Vérifiez les champs et réessayez.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Page testId="contact-page">
      <section className="mx-auto max-w-7xl px-6 pt-36 md:px-12 md:pt-48">
        <FadeUp><Meta>{c("contact.hero.meta")}</Meta></FadeUp>
        <h1 className="mt-6 font-serif text-5xl font-light leading-[1.08] tracking-tight md:text-7xl">
          <MaskedLine delay={0.2}>{c("contact.hero.t1")} <em className="text-clay">{c("contact.hero.t2")}</em></MaskedLine>
        </h1>
        <FadeUp delay={0.5} className="mt-10 max-w-2xl">
          <p className="text-base leading-relaxed text-smoke md:text-lg">
            {c("contact.hero.p")}
          </p>
        </FadeUp>
      </section>

      <section data-testid="contact-paths-section" className="mx-auto max-w-7xl px-6 py-20 md:px-12 md:py-28">
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {PATHS.map((p, i) => (
            <FadeUp key={p.subject} delay={0.07 * i} className="h-full">
              <button
                onClick={() => choosePath(p.subject)}
                data-testid={`contact-path-${i + 1}`}
                className="group flex h-full w-full flex-col rounded-[1.75rem] border border-linen bg-shell p-8 text-left transition-colors duration-500 hover:border-clay/40 hover:bg-sand/30"
              >
                <p.Icon className="h-7 w-7 text-clay transition-transform duration-500 group-hover:-translate-y-1" strokeWidth={1.25} />
                <h2 className="mt-6 font-serif text-xl font-light leading-snug tracking-tight md:text-2xl">{p.subject}</h2>
                <p className="mt-3 flex-1 text-sm leading-relaxed text-smoke">{p.text}</p>
                <span className="mt-6 inline-flex items-center gap-2 text-xs font-medium uppercase tracking-[0.15em] text-smoke transition-colors duration-300 group-hover:text-clay">
                  Choisir <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </span>
              </button>
            </FadeUp>
          ))}
        </div>

        <FadeUp delay={0.2}>
          <div data-testid="contact-sexologie-note" className="mt-10 flex flex-col items-start gap-6 rounded-[1.75rem] bg-sage/40 p-10 md:flex-row md:items-center md:justify-between">
            <div className="flex items-start gap-5">
              <HeartPulse className="mt-1 h-7 w-7 shrink-0 text-clay" strokeWidth={1.25} />
              <div>
                <h2 className="font-serif text-2xl font-light tracking-tight md:text-3xl">
                  {c("contact.sexo.titre")}
                </h2>
                <p className="mt-2 max-w-xl text-sm leading-relaxed text-smoke">
                  {c("contact.sexo.texte")}
                </p>
              </div>
            </div>
            <a href={links.sexualis || "#"} {...(links.sexualis ? { target: "_blank", rel: "noopener noreferrer" } : {})}
              data-testid="contact-sexualis-cta"
              className="group inline-flex shrink-0 items-center gap-2 rounded-full bg-clay px-7 py-3.5 text-xs font-medium uppercase tracking-[0.15em] text-cream transition-colors duration-300 hover:bg-charcoal">
              {c("contact.sexo.cta")}
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </a>
          </div>
        </FadeUp>

        {socials.length > 0 && (
          <FadeUp delay={0.25}>
            <div className="mt-10 rounded-[1.75rem] border border-linen bg-shell p-10">
              <p className="text-xs font-medium uppercase tracking-[0.2em] text-ash">{c("contact.social.titre")}</p>
              <div className="mt-4 flex flex-wrap gap-x-6 gap-y-3">
                {socials.map(({ key, label }) => (
                  <a
                    key={key}
                    href={links[key]}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`contact-social-${key}`}
                    className="group inline-flex items-center gap-1.5 text-sm text-charcoal transition-colors duration-300 hover:text-clay"
                  >
                    {label}
                    <ArrowUpRight className="h-3 w-3 opacity-0 transition-all duration-300 group-hover:opacity-100" />
                  </a>
                ))}
              </div>
            </div>
          </FadeUp>
        )}
      </section>

      <section id="formulaire" data-testid="contact-form-section" className="bg-shell py-24 md:py-32">
        <div className="mx-auto grid max-w-7xl gap-16 px-6 md:px-12 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <FadeUp><Meta>{c("contact.form.meta")}</Meta></FadeUp>
            <FadeUp delay={0.1}>
              <h2 className="mt-6 font-serif text-3xl font-light leading-snug tracking-tight md:text-4xl">
                {c("contact.form.t1")} <em className="text-clay">{c("contact.form.t2")}</em>
              </h2>
            </FadeUp>
            <FadeUp delay={0.2}>
              <p className="mt-6 text-sm leading-relaxed text-smoke md:text-base">
                {c("contact.form.p")}
              </p>
              <p className="mt-10 font-hand text-3xl text-clay">{c("contact.form.hand")}</p>
            </FadeUp>
          </div>

          <FadeUp delay={0.15} className="lg:col-span-8">
            <form data-testid="contact-form" onSubmit={onSubmit} className="space-y-8">
              <div className="grid gap-8 md:grid-cols-2">
                <Field label="Nom complet *" htmlFor="contact-name">
                  <input id="contact-name" data-testid="contact-name-input" required minLength={2}
                    value={form.name} onChange={set("name")} placeholder="Votre nom" className={inputCls} />
                </Field>
                <Field label="Courriel *" htmlFor="contact-email">
                  <input id="contact-email" data-testid="contact-email-input" required type="email"
                    value={form.email} onChange={set("email")} placeholder="vous@exemple.com" className={inputCls} />
                </Field>
              </div>
              <Field label="Sujet" htmlFor="contact-subject">
                <input id="contact-subject" data-testid="contact-subject-input"
                  value={form.subject} onChange={set("subject")}
                  placeholder="Médias, conférence, collaboration…" className={inputCls} />
              </Field>
              <Field label="Votre message *" htmlFor="contact-message">
                <textarea id="contact-message" data-testid="contact-message-input" required minLength={10} rows={5}
                  value={form.message} onChange={set("message")}
                  placeholder="Votre demande, votre contexte, vos échéances…" className={`${inputCls} resize-none`} />
              </Field>
              <SubmitButton loading={loading} testId="contact-submit-button">
                {c("contact.form.bouton")}
              </SubmitButton>
            </form>
          </FadeUp>
        </div>
      </section>
    </Page>
  );
}
